import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Delete as DeleteIcon, Warning as WarningIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export default function DeleteConfirmationDialog({
  open,
  onClose,
  onConfirm,
  scan,
  isDeleting,
}) {
  const { t } = useTranslation();

  const handleConfirm = () => {
    onConfirm(scan);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'error.main',
          pb: 1,
        }}
      >
        <WarningIcon color="error" />
        {t('scanned_certificates.delete_dialog.title')}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('scanned_certificates.delete_dialog.warning')}
          </Alert>

          <Typography variant="body1" sx={{ mb: 1 }}>
            {t('scanned_certificates.delete_dialog.confirm_message')}
          </Typography>

          <Box
            sx={{
              backgroundColor: 'grey.100',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('scanned_certificates.name')}
              :
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {scan?.originalFileName || '-'}
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('scanned_certificates.type')}
              :
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {(() => {
                if (scan?.certificateType === 'gauged_container') {
                  return t('scanned_certificates.gauged_container');
                }
                if (scan?.certificateType === 'tanker_truck') {
                  return t('scanned_certificates.tanker_truck');
                }
                return scan?.certificateType || '-';
              })()}
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('scanned_certificates.processed_at')}
              :
            </Typography>
            <Typography variant="body2">
              {(() => {
                if (!scan?.parser?.processedAt) return '-';

                const processedAt = scan.parser.processedAt.toDate
                  ? scan.parser.processedAt.toDate()
                  : new Date(scan.parser.processedAt);

                return processedAt.toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                });
              })()}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={isDeleting}
          variant="outlined"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isDeleting}
          variant="contained"
          color="error"
          startIcon={isDeleting ? null : <DeleteIcon />}
          sx={{
            minWidth: 120,
          }}
        >
          {isDeleting
            ? t('scanned_certificates.delete_dialog.deleting')
            : t('scanned_certificates.delete_dialog.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
